/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, FormHelperText, Grid, debounce } from "@mui/material"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { FormikHelpers, useFormik } from "formik"
import { useCallback, useMemo, useState } from "react"
import giftApis from "src/api/gifts"
import { GiftSelection, ShopSelection } from "src/components"
import {
  GIFTS_BY_CUSTOMER,
  GIFT_EXCHANGE_HISTORY,
  GIFT_ORDER,
} from "src/constants/queryKeys"
import { useAuthContext, useToastify } from "src/hooks"
import { theme } from "src/providers/ThemeProvider"
import { Button, Dialog, Text } from "src/shared"
import {
  GiftExchangeHistoryModel,
  GiftListByCustomerModel,
  ProductInsertModel,
  UpdateGiftOrderRequestModel,
} from "src/types"

interface IGiftExchangeDialogProps {
  open?: boolean
  onClose: () => void
  data?: GiftExchangeHistoryModel
}

const GiftExchangeDialog = ({
  open = false,
  onClose,
  data,
}: IGiftExchangeDialogProps) => {
  const [totalTemUsed, setTotalTemUsed] = useState(0)

  const queryClient = useQueryClient()
  const { profile, onConfirmPhoneNumber } = useAuthContext()
  const { showError, showSuccess } = useToastify()

  const onDialogClose = useCallback(() => {
    onClose()
  }, [onClose])

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (payload: UpdateGiftOrderRequestModel) =>
      giftApis.updateGiftExchange(payload),
  })

  const handleInvalidateQuery = useCallback(async () => {
    await onConfirmPhoneNumber(profile?.Mobile || "")
    queryClient.invalidateQueries({
      queryKey: [GIFT_ORDER],
    })
    queryClient.invalidateQueries({
      queryKey: [GIFT_EXCHANGE_HISTORY],
    })

    queryClient.invalidateQueries({
      queryKey: [GIFTS_BY_CUSTOMER],
    })
  }, [onConfirmPhoneNumber, queryClient, profile])

  const debounceInvalidateQuery = debounce(handleInvalidateQuery, 2000)

  const handleSubmit = useCallback(
    async (
      val: UpdateGiftOrderRequestModel,
      formikHelper: FormikHelpers<UpdateGiftOrderRequestModel>
    ) => {
      if (
        !!val.lstProduct &&
        !!val.lstProduct.length &&
        !!val.customerPhone &&
        !!val.stockCode
      ) {
        await mutateAsync(val, {
          onSuccess: async (d: any) => {
            if (d?.ErrorCode === 0) {
              showSuccess("Đổi quà thành công!")
              await debounceInvalidateQuery()
              onDialogClose()
              formikHelper.resetForm(undefined)
            } else if (d?.ErrorCode === 1 && !!d?.ErrorMessage) {
              showError(d?.ErrorMessage)
            } else {
              showError("Đã xảy ra lỗi, vui lòng thử lại!")
            }
          },
          onError: () => {
            showError("Đã xảy ra lỗi, vui lòng thử lại!")
          },
        })
      }
    },
    [
      mutateAsync,
      showError,
      onDialogClose,
      debounceInvalidateQuery,
      showSuccess,
    ]
  )

  const { setFieldValue, submitForm, values } =
    useFormik<UpdateGiftOrderRequestModel>({
      initialValues: {
        createUser: "",
        customerPhone: data?.CustomerPhone || "",
        lstProduct: [],
        stockCode: "",
        oldOrderId: data?.OrderId || 0,
      },
      onSubmit: debounce(handleSubmit, 1000),
    })

  const handleGiftChange = (vals?: GiftListByCustomerModel[]) => {
    if (!vals || !vals.length) {
      setFieldValue("lstProduct", undefined)
      setTotalTemUsed(0)
      return
    }
    const giftList = vals.map(
      v =>
        ({
          productCode: v.ProductCode,
          productName: v.ProductName,
          productImage: v.ProductDefaultImage,
          quantity: 1,
          stockCode: v.StockCode,
        }) as ProductInsertModel
    )
    setFieldValue("lstProduct", giftList || undefined)

    const totalTemUsed = vals.reduce((a, b) => a + (b.TemQuantity || 0), 0)
    setTotalTemUsed(totalTemUsed)
  }

  const onShopChange = (storeCode?: string) => {
    setFieldValue("stockCode", storeCode)
    setFieldValue("lstProduct", undefined)
  }

  const disableSubmit = useMemo(
    () =>
      !values.lstProduct ||
      !values.lstProduct.length ||
      !values.stockCode ||
      !!(totalTemUsed > (profile?.TemQuantity || 0)),
    [values, profile, totalTemUsed]
  )

  const giftValue = useMemo(
    () => values?.lstProduct?.map(x => x.productCode) || [],
    [values]
  )

  const shopValue = useMemo(() => values?.stockCode || "", [values])

  return (
    <Dialog disablePortal open={open} onClose={onDialogClose}>
      <Grid
        rowGap={theme.spacing(2.5)}
        container
        alignItems="center"
        direction="column"
      >
        <Text
          mb={1.5}
          color={theme.palette.secondary.main}
          fontSize={24}
          lineHeight="31.65px"
          fontWeight={700}
        >
          Thay đổi quà tặng
        </Text>
        <ShopSelection value={shopValue} onChange={onShopChange} />
        <Box
          width="100%"
          flexDirection="column"
          gap={1}
          justifyContent="center"
        >
          <GiftSelection
            value={giftValue}
            key={values.stockCode}
            stockCode={values?.stockCode}
            onChange={handleGiftChange}
          />
          {totalTemUsed > (profile?.TemQuantity || 0) && (
            <FormHelperText error>
              Tổng số tem được sử dụng là {totalTemUsed} vượt quá số lượng tem
              bạn đang có là {profile?.TemQuantity}.
            </FormHelperText>
          )}
        </Box>
        <Button
          onClick={submitForm}
          disabled={isPending || disableSubmit}
          loading={isPending}
        >
          Xác nhận
        </Button>
      </Grid>
    </Dialog>
  )
}

export default GiftExchangeDialog
